import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Mail, CheckCircle2, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth.js";
import { Button, Field } from "../components/ui.js";
import { Wordmark } from "../components/brand/Logo.js";

type Mode = "signin" | "signup";

const schema = z.object({
  full_name: z.string().optional(),
  email: z.string().email("Geçerli bir e-posta girin."),
  password: z.string().min(6, "Şifre en az 6 karakter olmalı."),
});
type FormValues = z.infer<typeof schema>;

export default function Login() {
  const { signInWithPassword, signUpWithPassword, signInWithMagicLink } = useAuth();
  const [mode, setMode] = useState<Mode>("signin");
  const [serverError, setServerError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), mode: "onBlur" });

  async function onSubmit(values: FormValues) {
    setServerError(null);
    setNotice(null);
    const { error } =
      mode === "signin"
        ? await signInWithPassword(values.email, values.password)
        : await signUpWithPassword(values.email, values.password, values.full_name);
    if (error) {
      setServerError(error);
      return;
    }
    if (mode === "signup") {
      setNotice(
        "Kaydınız alındı. E-posta doğrulaması açıksa gelen kutunuzu kontrol edin; değilse otomatik giriş yapılıyor.",
      );
    }
  }

  async function onMagicLink() {
    setServerError(null);
    const email = getValues("email");
    if (!z.string().email().safeParse(email).success) {
      setServerError("Önce geçerli bir e-posta girin.");
      return;
    }
    const { error } = await signInWithMagicLink(email);
    if (error) setServerError(error);
    else setMagicSent(true);
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* ── Sol: marka paneli ────────────────────────────────────────────── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-slate-800/60 p-12 lg:flex">
        <div className="animate-fade">
          <Wordmark />
        </div>

        <div className="relative z-10 max-w-md animate-rise">
          <p className="eyebrow mb-5">Yöneticinin Sabah İstihbaratı</p>
          <h1 className="font-serif text-5xl leading-[1.05] text-slate-50">
            Gündemi siz takip etmeyin.
            <span className="mt-2 block text-gold">Z-Suite sizin için süzsün.</span>
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-400">
            50+ Türk ve dünya kaynağını her sabah tarar, yapay zekâyla sizin
            konularınıza göre 5 maddeye indirir. Kahvenizle birlikte, 3 dakikada.
          </p>

          {/* Brifing önizleme kartı */}
          <div className="mt-10 rounded-xl border border-slate-700/50 bg-ink-800/60 p-5 backdrop-blur-sm">
            <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
              <Sparkles className="h-3.5 w-3.5 text-gold" />
              30 Haziran · Bugünün brifingi
            </div>
            <ul className="space-y-2.5 text-sm">
              {[
                ["08", "Bütçe ilk çeyrekte 526,8 milyar TL açık verdi"],
                ["07", "Çin, Orta Doğu'daki çatışmanın açık kazananı"],
                ["07", "Petrol, ABD-İran görüşmeleriyle geriledi"],
              ].map(([score, text]) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gold/30 bg-gold/10 font-serif text-xs text-gold">
                    {score}
                  </span>
                  <span className="text-slate-300">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="relative z-10 text-xs text-slate-600">Bir Zeniq ürünü · İstanbul</p>
      </aside>

      {/* ── Sağ: auth formu ──────────────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-rise">
          <div className="mb-8 lg:hidden">
            <Wordmark />
          </div>

          <p className="eyebrow mb-2">{mode === "signin" ? "Tekrar hoş geldiniz" : "Hesap oluşturun"}</p>
          <h2 className="mb-7 font-serif text-3xl text-slate-50">
            {mode === "signin" ? "Panele giriş" : "Z-Suite'e katılın"}
          </h2>

          {magicSent ? (
            <div className="rounded-xl border border-gold/30 bg-gold/5 p-5 text-sm text-slate-200">
              <CheckCircle2 className="mb-2 h-5 w-5 text-gold" />
              Giriş bağlantısı <strong>{getValues("email")}</strong> adresine gönderildi.
              E-postanızdaki bağlantıya tıklayarak giriş yapın.
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {mode === "signup" && (
                <Field
                  label="Ad Soyad"
                  placeholder="Örn. Onur Yılmaz"
                  autoComplete="name"
                  {...register("full_name")}
                />
              )}
              <Field
                label="E-posta"
                type="email"
                placeholder="siz@sirket.com"
                autoComplete="email"
                error={errors.email?.message}
                {...register("email")}
              />
              <Field
                label="Şifre"
                type="password"
                placeholder="••••••••"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                error={errors.password?.message}
                {...register("password")}
              />

              {serverError && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {serverError}
                </p>
              )}
              {notice && (
                <p className="rounded-lg border border-gold/30 bg-gold/5 px-3 py-2 text-sm text-slate-200">
                  {notice}
                </p>
              )}

              <Button type="submit" loading={isSubmitting} className="w-full">
                {mode === "signin" ? "Giriş yap" : "Hesap oluştur"}
                <ArrowRight className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-700/50" />
                <span className="text-xs text-slate-500">veya</span>
                <div className="h-px flex-1 bg-slate-700/50" />
              </div>

              <Button type="button" variant="outline" className="w-full" onClick={onMagicLink}>
                <Mail className="h-4 w-4" />
                E-posta ile bağlantı gönder
              </Button>
            </form>
          )}

          <p className="mt-8 text-center text-sm text-slate-500">
            {mode === "signin" ? "Hesabınız yok mu?" : "Zaten üye misiniz?"}{" "}
            <button
              type="button"
              onClick={() => {
                setMode(mode === "signin" ? "signup" : "signin");
                setServerError(null);
                setNotice(null);
              }}
              className="font-medium text-gold transition-colors hover:text-gold-300"
            >
              {mode === "signin" ? "Kayıt olun" : "Giriş yapın"}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
